import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { GUIDE_ROUTES } from '@/features/guide/constants/routes';
import { useRewardsSummary } from '../hooks/useGrowth';
import { TRIPPY_SRC } from './PromoPostCanvas';

/** Compact Trippy card for the guide home page; links to the Rewards page. */
export function RewardsTeaserCard() {
  const { t } = useTranslation('rewards');
  const { data } = useRewardsSummary();

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        // Matches the home hero it sits beside, and stretches to its height.
        height: '100%',
        borderRadius: 'var(--radius-xl)',
        color: '#FFFFFF',
        background:
          'radial-gradient(ellipse 60% 80% at 92% 100%, rgba(200,169,110,0.22) 0%, transparent 70%), linear-gradient(125deg, #1A2E1A 0%, #213821 55%, #2F4B2F 100%)',
        boxShadow: 'var(--shadow-lg)',
        // Text and Trippy get their own columns so the copy can never run under him.
        display: 'grid',
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr) 130px',
          sm: 'minmax(0, 1fr) 220px',
          lg: 'minmax(0, 1fr) minmax(130px, 40%)',
        },
        minHeight: { xs: 200, lg: 300 },
      }}
    >
      <Box sx={{ p: { xs: 3, md: 4.5 }, pr: { xs: 1, md: 1.5 }, alignSelf: 'center', position: 'relative', zIndex: 1 }}>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.18em', color: '#C8A96E' }}>
          {t('teaser.eyebrow')}
        </Typography>
        <Typography
          sx={{
            mt: 0.75,
            color: '#FFFFFF',
            fontFamily: '"Playfair Display", Georgia, serif',
            fontWeight: 700,
            fontSize: { xs: '1.25rem', sm: '1.6rem' },
            lineHeight: 1.2,
          }}
        >
          {t('teaser.title')}
        </Typography>
        <Typography sx={{ mt: 1, fontSize: '0.92rem', lineHeight: 1.6, color: 'rgba(255,255,255,0.78)' }}>
          {data?.wallet.available ? t('teaser.credits', { credits: data.wallet.available.toLocaleString('en-IN') }) : t('teaser.body')}
        </Typography>
        <Button
          component={RouterLink}
          to={GUIDE_ROUTES.REWARDS}
          size="medium"
          endIcon={<ArrowForwardRoundedIcon />}
          sx={{ mt: 2.5, borderRadius: 999, fontWeight: 800, px: 2.75, bgcolor: '#C8A96E', color: '#142214', '&:hover': { bgcolor: '#E9D7AE' } }}
        >
          {t('teaser.cta')}
        </Button>
      </Box>
      <Box sx={{ position: 'relative', alignSelf: 'end', display: 'flex', justifyContent: 'center', pr: { xs: 1, sm: 2 } }}>
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: '50%',
            bottom: '-18%',
            width: '105%',
            aspectRatio: '1',
            transform: 'translateX(-50%)',
            borderRadius: '50%',
            background: 'radial-gradient(circle at 45% 40%, #F5EBD3 0%, #E9D7AE 45%, #C8A96E 100%)',
            boxShadow: '0 0 0 14px rgba(200,169,110,0.12)',
          }}
        />
        <Box
          component="img"
          src={TRIPPY_SRC}
          alt=""
          sx={{
            position: 'relative',
            display: 'block',
            width: '100%',
            height: 'auto',
            maxHeight: 260,
            objectFit: 'contain',
            objectPosition: 'bottom',
            filter: 'drop-shadow(0 12px 20px rgba(0,0,0,0.3))',
          }}
        />
      </Box>
    </Box>
  );
}
